import { TranslateService } from 'Public_old/src/app/Common/Services/translate.service';
import { Component, Input, Output, EventEmitter, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, Validators, UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { PaymentServiceV2 } from '../Services/api/v2/Units/payment.service';
import { PaymentServiceApiModel } from '../Services/api/v2/Models/PaymentServiceApiModel';

export type PaymentStep = 'paymentOptions' | 'paymentFrame' | 'paymentFailed' | 'paymentCompleted' | 'paymentCompletedNoReceipt';

@Component({
	standalone: false,
	selector: 'visiba-payment',
	template: /*language=html*/ `
		<div class="paymentBox" *ngIf="paymentData !== null">
			<div *ngIf="currentStep === 'paymentOptions' || prevStep === 'paymentOptions'" class="paymentOptions">
				<form
					novalidate
					(ngSubmit)="submitPaymentOptions()"
					[formGroup]="formPaymentoptions"
					[class.ng-submitted]="formPaymentoptions['submitted']"
				>
					<div>
						<h2 class="paymentTitle">{{ 'LicenceHolder.VisibaPayment.Title.HowYouWantToPay' | translate }}</h2>
						<p>{{ 'LicenceHolder.VisibaPayment.Phrase.PaymentProviderInfo' | translate }}</p>
					</div>
					<div class="paymentChoices">
						<visiba-input
							formControlName="paymentType"
							value="Name"
							label="Name"
							[options]="paymentData"
							[(ngModel)]="paymentTypeValue"
							type="radio"
							(onChange)="onPaymentTypeChange($event)"
						></visiba-input>
					</div>
					<div class="continueActions">
						<div class="continue">
							<button type="submit" class="btn">{{ 'LicenceHolder.VisibaPayment.Button.ToPayment' | translate }}</button>
						</div>
						<div class="cancel" [class.hide]="!showCancel">
							<a (click)="abort()">{{ 'Common.Button.Abort' | translate }}</a>
						</div>
					</div>
				</form>
			</div>

			<div *ngIf="currentStep === 'paymentFrame' || prevStep === 'paymentFrame'" class="paymentFrame">
				<visiba-svg class="loadingIcon" a="Loader"></visiba-svg>
				<iframe [src]="paymentUrl"> </iframe>
			</div>

			<div *ngIf="currentStep === 'paymentFailed' || prevStep === 'paymentFailed'" class="paymentFailed">
				<div>
					<visiba-svg a="XCircle"></visiba-svg>
					<h2 class="paymentTitle">{{ 'LicenceHolder.VisibaPayment.Title.PaymentAborted' | translate }}</h2>
				</div>
				<div class="failedMessage" [class.span13]="showFailedRetryButton">
					<p>{{ this.errorMessage }}</p>
				</div>
				<div class="continueActions">
					<div *ngIf="showFailedRetryButton">
						<div class="btn" (click)="retry()">
							{{ customFailRetry !== null ? customFailRetry.buttonText : ('LicenceHolder.VisibaPayment.Button.Retry' | translate) }}
						</div>
					</div>
					<div class="cancel" [class.hide]="!showCancel">
						<a (click)="abort()">{{ 'Common.Button.Abort' | translate }}</a>
					</div>
				</div>
			</div>
			<ng-container *ngIf="showReciept">
				<visiba-succesBox
					class="paymentCompleted"
					*ngIf="currentStep === 'paymentCompleted' || prevStep === 'paymentCompleted'"
					mode="href"
					[href]="attemptData.receiptUrl"
					[title]="'LicenceHolder.VisibaPayment.Title.PaymentThanks' | translate"
					[info]="'LicenceHolder.BookingList.Phrase.BookConfirmation' | translate"
					[button]="'LicenceHolder.VisibaPayment.Button.GetReceiptAsPdf' | translate"
					(onToggle)="finish()"
				>
				</visiba-succesBox>

				<visiba-succesBox
					class="paymentCompleted paymentCompletedNoReceipt"
					*ngIf="currentStep === 'paymentCompletedNoReceipt' || prevStep === 'paymentCompletedNoReceipt'"
					mode="none"
					[href]="null"
					[title]="'LicenceHolder.VisibaPayment.Title.PaymentThanksNoReceipt' | translate"
					[info]="'LicenceHolder.VisibaPayment.Phrase.ReceiptSent_' + completedWithMethod | translate"
					(onToggle)="finish()"
				>
				</visiba-succesBox>
			</ng-container>
		</div>
	`,
	styleUrls: ['../Css/Components/visibaPayment.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class VisibaPaymentComponent {
	@Output('onAbort') abortEvent: EventEmitter<void> = new EventEmitter<void>();
	@Output('onFinish') finishEvent: EventEmitter<void> = new EventEmitter<void>();
	@Output('onStepChange') stepChangeEvent = new EventEmitter<{ step: PaymentStep; data: any }>();
	@Input('showCancel') showCancel = false;
	@Input('billId') billId: number = null;
	@Input('showReciept') showReciept = true;

	paymentData: PaymentServiceApiModel[] = null;

	paymentTypeValue: string = null;
	selectedPaymentType: PaymentServiceApiModel = null;
	paymentUrl: any;
	completedWithMethod: string = null;

	formPaymentoptions: UntypedFormGroup;
	errorMessage: string = null;
	showFailedRetryButton: boolean = true;
	currentStep: PaymentStep = 'paymentOptions';
	prevStep: PaymentStep = 'paymentOptions';
	customFailRetry: VisibaPaymentComponent.CustomFailRetry = null;

	attemptData: VisibaPaymentComponent.AttemptData = null;

	constructor(
		private _builder: UntypedFormBuilder,
		private _sanitizer: DomSanitizer,
		private _paymentService: PaymentServiceV2,
		private translateService: TranslateService,
	) {
		this.formPaymentoptions = _builder.group({
			paymentType: new UntypedFormControl(null, Validators.compose([Validators.required])),
		});
	}
	ngOnInit() {
		this.init();
	}

	init() {
		this._paymentService.get(this.billId).subscribe((response) => {
			this.paymentData = response.data;

			this.setCurrentStep('paymentOptions');
		});
	}

	setCurrentStep(step: PaymentStep, stepData: any = null) {
		this.prevStep = this.currentStep;
		if (step === 'paymentCompleted' && !stepData.hasReceipt) step = 'paymentCompletedNoReceipt';

		this.currentStep = step;
		this.stepChangeEvent.emit({ step: this.currentStep, data: stepData });

		window.setTimeout(() => {
			{
				let selector: string = '.' + this.prevStep;
				let element: HTMLElement = document.querySelector(selector) as HTMLElement;
				if (element !== null) {
					element.style.opacity = '0';
					element.style.pointerEvents = 'none';
				}
			}
			{
				let selector: string = '.' + step;
				let element: HTMLElement = document.querySelector(selector) as HTMLElement;
				if (!element) return;
				let height = element.clientHeight;
				element.parentElement.style.height = height.toString() + 'px';
				element.style.pointerEvents = 'all';

				if (step === 'paymentCompleted' || step === 'paymentCompletedNoReceipt') {
					element.parentElement.style.marginBottom = '10px';
				}

				window.setTimeout(() => {
					element.style.opacity = '1';
					this.prevStep = null;
				}, 250);
			}
		}, 0);
	}

	onPaymentTypeChange(paymentType: any) {
		this.selectedPaymentType = this.paymentData.find((x) => x.Name === paymentType);
		this.paymentUrl = this._sanitizer.bypassSecurityTrustResourceUrl(this.selectedPaymentType.Url);
	}

	abort() {
		this.abortEvent.emit();
	}

	finish() {
		this.finishEvent.emit();
	}

	close(completeFunction: () => void = null) {
		let selector: string = '.' + this.currentStep;
		let element: HTMLElement = document.querySelector(selector) as HTMLElement;
		let height = 0;
		element.parentElement.style.height = height.toString() + 'px';
		element.style.pointerEvents = 'none';
		element.style.opacity = '0';
		this.prevStep = null;

		if (completeFunction !== null) {
			window.setTimeout(() => {
				completeFunction();
			}, 500);
		}
	}

	submitPaymentOptions() {
		this.formPaymentoptions['submitted'] = true;
		if (!this.formPaymentoptions.valid) {
			return;
		}
		this.setCurrentStep('paymentFrame');
	}

	retry() {
		if (this.customFailRetry !== null) {
			this.customFailRetry.func();
		} else {
			this.setCurrentStep('paymentOptions');
		}
	}

	setError(errorMessage: string, showRetryButton = true, customRetry: VisibaPaymentComponent.CustomFailRetry = null) {
		this.errorMessage = errorMessage;
		this.showFailedRetryButton = showRetryButton;
		this.customFailRetry = customRetry;
		this.setCurrentStep('paymentFailed');
	}

	ngAfterViewInit() {
		window.addEventListener('message', this.paymentMessage, false);
	}
	paymentMessage = (message) => {
		switch (message.data.event) {
			case 'Success':
				this.completedWithMethod = message.data.data.method;
				this.attemptData = message.data.data.info;
				this.setCurrentStep('paymentCompleted', message.data.data.info);
				break;
			case 'Error':
				if (typeof message.data.data !== 'undefined' && message.data.data !== null) {
					this.setError(message.data.data.message);
				} else {
					this.setError(this.translateService.get('LicenceHolder.VisibaPayment.Error.UnknownError'));
				}
				break;
			case 'Cancel':
				if (typeof message.data.data !== 'undefined' && message.data.data !== null) {
					this.setError(message.data.data.message);
				} else {
					this.setError(this.translateService.get('LicenceHolder.VisibaPayment.Error.CanceledByUser'));
				}
				break;
		}
	};
	ngOnDestroy() {
		window.removeEventListener('message', this.paymentMessage, false);
	}

	public reset() {
		this.currentStep = null;
		this.prevStep = null;

		this.paymentData = null;

		this.paymentTypeValue = null;
		this.selectedPaymentType = null;
		this.paymentUrl = null;
		this.formPaymentoptions = this._builder.group({
			paymentType: new UntypedFormControl(null, Validators.compose([Validators.required])),
		});
		this.errorMessage = null;
		this.showFailedRetryButton = true;
		this.customFailRetry = null;

		this.setCurrentStep('paymentOptions');
	}
}

export module VisibaPaymentComponent {
	export class CustomFailRetry {
		constructor(
			public buttonText: string,
			public func: () => void,
		) {}
	}

	export class AttemptData {
		receiptUrl: string | null;
		hasReceipt: boolean;
	}
}
